import React, { useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogDescription,
  type DialogContentProps
} from "@radix-ui/react-dialog";
import { cn } from "@design-lab/shared/lib/tailwindcss";
import { X } from "lucide-react";

export interface ModalProps {
  isOpen: boolean;
  onClose?: () => void;
  children: React.ReactNode;
  className?: string;
  backdropClassName?: string;
  closeOnEsc?: boolean;
  closeOnBackdropClick?: boolean;
  title?: string;
  description?: string;
}

export function Modal({
  isOpen,
  onClose,
  children,
  className,
  backdropClassName,
  closeOnEsc = true,
  closeOnBackdropClick = true,
  title = "",
  description = ""
}: ModalProps) {
  // Handle document body overflow
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  const handleEscapeKeyDown = (e: KeyboardEvent) => {
    if (!closeOnEsc) {
      e.preventDefault();
    }
  };

  const handleInteractOutside: DialogContentProps["onInteractOutside"] = e => {
    e.preventDefault();
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (closeOnBackdropClick) {
      e.stopPropagation();
      onClose?.();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose?.()}>
      <DialogPortal>
        <DialogOverlay
          className={cn(
            // Replaced fixed with absolute to allow for the modal to be positioned relative to the viewport in Mobile App
            "bg-foreground/50 absolute inset-0 z-50 flex items-center justify-center",
            backdropClassName
          )}
          onClick={handleBackdropClick}
        />
        <DialogContent
          className={cn(
            // Replaced fixed with absolute to allow for the modal to be positioned relative to the viewport in Mobile App
            "bg-background absolute left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 overflow-hidden rounded-lg shadow-lg",
            "w-lg h-[50vh]",
            className
          )}
          onEscapeKeyDown={handleEscapeKeyDown}
          onInteractOutside={handleInteractOutside}
          role="dialog"
          aria-modal="true"
        >
          <DialogTitle className="sr-only">{title}</DialogTitle>
          <DialogDescription className="sr-only">{description}</DialogDescription>
          {children}
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
}

export const ModalHeader = ({
  children,
  className,
  onClose
}: {
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
}) => (
  <div className={cn("relative flex items-center justify-center border-b p-6", className)}>
    <div className="text-lg font-semibold">{children}</div>
    {onClose && (
      <div className="absolute right-6 flex">
        <button
          data-testid="modal-close-button"
          onClick={onClose}
          className="cursor-pointer text-gray-500 hover:text-gray-700 focus:outline-none"
        >
          <span className="sr-only">Close</span>
          <X className="h-6 w-6" />
        </button>
      </div>
    )}
  </div>
);

export const ModalBody = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={cn("overflow-auto p-4", className)}>{children}</div>;

export const ModalFooter = ({
  children,
  className
}: {
  children: React.ReactNode;
  className?: string;
}) => <div className={cn("border-t p-4", className)}>{children}</div>;
